import { BrandSelect } from "components/BrandSelect/BrandSelect"
import { ImageUpload } from "components/ImageUpload"
import { ProductCategoriesLink } from "components/Navbar/types"
import PageLoader from "components/PageLoader"
import { ProductCardPreview } from "components/ProductCardPreview"
import { useAppDispatch, useAppSelector } from "hooks/storeHooks"
import { useCurrentId } from "hooks/useCurrentId"
import { ChangeEvent, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { tintVTOService } from "services/tintvto"
import { clearGlasses, setGlasses, setGlassesOptionsByKey } from "store/product/productSlice"
import { getFileInfo } from "utils/logicWithFiles"
import {
  BaseHeaderLayout,
  Box,
  Button,
  ContentLayout,
  Flex,
  Link,
  LinkButton,
  SingleSelect,
  SingleSelectOption,
  Typography,
} from "@strapi/design-system"
import { ArrowLeft, Check } from "@strapi/icons"
import { TextInputWithInfoTooltip } from "../../ProductPage/TextInputWithInfoTooltip"
import { IntegrationType } from "types"
import { z } from "zod"
import { Errors } from "pages/Products/ProductPage/Errors"
import { ProductTranslationsModal } from "components/ProductTranslations/ProductTranslationsModal"
import { ProductTranslationsData } from "components/ProductTranslations/ProductTranslations"

interface GlassesInfoPage {
  add?: boolean
}

const createGlassSchema = (t: (key: string) => string) => {
  return z.object({
    name: z.string().min(1, t("errors.nameRequired")),
    brand: z.string().min(1, t("errors.brandRequired")),
    sku: z.string().min(1, t("errors.skuRequired")),
    cover: z.string().min(1, t("errors.coverImageRequired")).optional(),
    merchant: z.string().optional(),
  })
}

export const GlassesInfoPage = ({ add }: GlassesInfoPage) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const glasses = useAppSelector((state) => state.products.glasses)
  const userInfo = useAppSelector((state) => state.user.userInfo)
  const productId = useCurrentId()
  const [loading, setIsLoading] = useState(false)
  const [merchantIds, setMerchantIds] = useState([])
  const [translations, setTranslations] = useState<ProductTranslationsData>({
    productName: [],
    skuNames: {},
  })
  const [pendingTranslations, setPendingTranslations] = useState<ProductTranslationsData>({
    productName: [],
    skuNames: {},
  })
  const [isTranslationsModalOpen, setIsTranslationsModalOpen] = useState(false)
  const [savedLanguages, setSavedLanguages] = useState<string[]>([])

  const initGlasses = async () => {
    setIsLoading(true)

    if (userInfo.groups.includes("Admin")) {
      const { data } = await tintVTOService.merchants()
      setMerchantIds(data?.merchants?.map((m) => ({ id: m.id, name: m.name })) || [])
    }
    if (!glasses.dataFilling)
      if (productId && !add) {
        const product = await tintVTOService.productByIdV2(productId)
        dispatch(
          setGlasses({
            ...product,
            upc: "",
            cover: product.cover || "",
            renderingOptions: product.renderingOptions,
            productImageFile: getFileInfo(product.cover),
            dataFilling: true,
          })
        )

        const skuIds = product.colors?.map((c) => c.skuId || c.sku).filter(Boolean) || []
        if (skuIds.length > 0) {
          try {
            const translationsData = await tintVTOService.getProductTranslations(productId, skuIds)
            const loadedProductName = translationsData.alternateName || []
            const loadedSkuNames = (translationsData.alternateSKUsName || []).reduce((acc, item) => {
              acc[item.skuId] = item.skuI8N || []
              return acc
            }, {} as Record<string, Array<{ code: string; name: string }>>)

            setTranslations({
              productName: loadedProductName,
              skuNames: loadedSkuNames,
            })
            setSavedLanguages(loadedProductName.map((t) => t.code))
          } catch (error) {
            console.error("Failed to load translations:", error)
          }
        }
      } else {
        dispatch(clearGlasses())
      }
    setIsLoading(false)
  }
  const glassesSchema = createGlassSchema(t)
  const [errors, setErrors] = useState<z.ZodFormattedError<z.infer<typeof glassesSchema>> | null>(
    null
  )

  useEffect(() => {
    initGlasses()
    return () => {
      const allowedRoutes = ["/glasses/add", "/glasses/edit"]
      if (!allowedRoutes.some((route) => window.location.pathname.startsWith(route)))
        dispatch(clearGlasses())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleNextStep = () => {
    dispatch(setGlassesOptionsByKey({ key: "dataFilling", value: true }))
    // Store translations in session storage to pass to next step
    const translationsToStore = add ? pendingTranslations : translations
    if (translationsToStore) {
      sessionStorage.setItem("glassesTranslations", JSON.stringify(translationsToStore))
      sessionStorage.setItem("glassesTranslationsMode", add ? "pending" : "existing")
    }
    if (productId) navigate(`/glasses/edit/info?id=${productId}`, { replace: true })
    else navigate(`/glasses/add/info`, { replace: true })
  }

  const imagePlaceHolder = "https://placehold.co/400x400?text=Product+Cover+Image"

  const validateForm = () => {
    try {
      glassesSchema.parse({ ...glasses, cover: glasses.cover || "" })
      setErrors(null)
      return true
    } catch (error) {
      if (error instanceof z.ZodError) setErrors(error.format())
      console.log(error.format())
      return false
    }
  }

  const handleFileChange = async (file: File) => {
    if (file) {
      const imageUrl = URL.createObjectURL(file)
      dispatch(
        setGlasses({
          ...glasses,
          file: file,
          cover: imageUrl,
        })
      )
      delete errors?.cover
      setErrors(errors)
    }
  }

  return (
    <Box>
      <BaseHeaderLayout
        title={add ? t("glassesInfoPage.addProduct") : t("glassesInfoPage.editProduct")}
        as={"h1"}
        primaryAction={
          <Flex gap={2}>
            <LinkButton
              variant="secondary"
              onClick={() => {
                navigate(ProductCategoriesLink.GLASSES, { replace: true })
              }}
            >
              {t("glassesInfoPage.cancel")}
            </LinkButton>
            <Button
              startIcon={<Check />}
              onClick={() => {
                if (validateForm()) handleNextStep()
              }}
              disabled={errors !== null && Object.keys(errors).length > 1}
            >
              {t("glassesInfoPage.next")}
            </Button>
          </Flex>
        }
        navigationAction={
          <Link
            startIcon={<ArrowLeft />}
            onClick={() => {
              navigate(ProductCategoriesLink.GLASSES)
            }}
          >
            {t("glassesInfoPage.backToProducts")}
          </Link>
        }
      />

      <ContentLayout>
        {!loading && glasses ? (
          <Box className="flex gap-4">
            <div className={"flex-1 gap-4 flex flex-col"}>
              <Box
                padding={8}
                hasRadius
                shadow="tableShadow"
                background="neutral0"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  minWidth: "520px",
                  maxHeight: "600px",
                  gap: 8,
                }}
              >
                <Box className="flex items-center justify-between">
                  <Typography variant="beta">{t("glassesInfoPage.productCardInfo")}</Typography>
                  {(productId || add) && (
                    <Button
                      variant="secondary"
                      size="S"
                      onClick={() => setIsTranslationsModalOpen(true)}
                    >
                      {t("productPage.modifyTranslations")}
                    </Button>
                  )}
                </Box>
                <Box
                  style={{
                    gap: "8px",
                  }}
                >
                  <TextInputWithInfoTooltip
                    label={t("glassesInfoPage.productName")}
                    name="product_name"
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      dispatch(setGlassesOptionsByKey({ key: "name", value: e.target.value }))
                      delete errors?.name
                      setErrors(errors)
                    }}
                    value={glasses.name}
                    errors={errors?.name?._errors}
                    description={t("glassesInfoPage.productNameDescription")}
                  />

                  <BrandSelect
                    value={glasses.brand}
                    onChange={(value: string) => {
                      dispatch(setGlassesOptionsByKey({ key: "brand", value }))
                      delete errors?.brand
                      setErrors(errors)
                    }}
                    errors={errors?.brand?._errors}
                    description={t("productPage.selectAnExistingBrand")}
                    label="Brand"
                  />
                </Box>
                {userInfo.groups.includes("Admin") && (
                  <>
                    <SingleSelect
                      label={t("productPage.merchantIdLabel")}
                      onChange={(value: string) => {
                        dispatch(setGlassesOptionsByKey({ key: "merchant", value }))
                        delete errors?.merchant
                        setErrors(errors)
                      }}
                      value={glasses.merchant}
                    >
                      {merchantIds.map((m) => (
                        <SingleSelectOption key={m.id} value={m.id}>
                          {m.name} - {m.id}
                        </SingleSelectOption>
                      ))}
                    </SingleSelect>
                    <Errors {...errors?.merchant} />
                  </>
                )}
                <TextInputWithInfoTooltip
                  label={t("glassesInfoPage.productPageUrl")}
                  description={t("glassesInfoPage.productPageUrlLabel")}
                  name="url"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    dispatch(
                      setGlassesOptionsByKey({
                        key: "url",
                        value: e.target.value,
                      })
                    )
                  }}
                  value={glasses.url ?? ""}
                  isOptional={true}
                />

                <ImageUpload onFileChange={handleFileChange} errors={errors?.cover?._errors} />
              </Box>
              <Box
                padding={8}
                hasRadius
                shadow="tableShadow"
                background="neutral0"
                style={{
                  display: "flex",
                  alignItems: "start",
                  justifyContent: "start",
                  flexDirection: "column",
                  maxHeight: "175px",
                }}
              >
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 18,
                  }}
                >
                  <Typography variant="beta">{t("glassesInfoPage.virtualTryOn")}</Typography>
                  <Box
                    style={{
                      gap: 16,
                    }}
                  >
                    <Box>
                      <TextInputWithInfoTooltip
                        label="SKU"
                        name="sku"
                        disabled={userInfo.integrationType !== IntegrationType.COMMON}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          const newSku = e.target.value
                          dispatch(setGlassesOptionsByKey({ key: "sku", value: newSku }))
                          // Also update SKU in colors array if it exists
                          if (glasses.colors && glasses.colors.length > 0) {
                            const updatedColors = glasses.colors.map((color) => ({
                              ...color,
                              sku: newSku,
                            }))
                            dispatch(setGlassesOptionsByKey({ key: "colors", value: updatedColors }))
                          }
                          delete errors?.sku
                          setErrors(errors)
                        }}
                        errors={errors?.sku?._errors}
                        value={glasses?.sku}
                        description={t("glassesInfoPage.sku")}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </div>
            <Box
              padding={8}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 32,
                justifyContent: "start",
                alignItems: "center",
                padding: "0",
                maxWidth: "300px",
              }}
            >
              <Box
                hasRadius
                shadow="tableShadow"
                style={{
                  borderColor: "rgb(74, 74, 106)",
                }}
                className={"!border-1 !border-solid   !p-4"}
              >
                <ProductCardPreview
                  withoutVariantName={true}
                  imageSrc={glasses?.cover || imagePlaceHolder}
                  sku={""}
                  brand={glasses.brand ?? ""}
                  productName={glasses?.name ?? ""}
                  color={glasses?.colors?.[0]?.color}
                />
              </Box>
            </Box>
          </Box>
        ) : (
          <PageLoader />
        )}
      </ContentLayout>
      {(productId || add) && (
        <ProductTranslationsModal
          isOpen={isTranslationsModalOpen}
          onClose={() => setIsTranslationsModalOpen(false)}
          productId={productId || ""}
          merchantId={glasses.merchant || userInfo.merchantId}
          skuIds={glasses.colors?.map((c) => c.skuId || c.sku).filter(Boolean) || []}
          defaultProductName={glasses.name}
          defaultSkuNames={glasses.colors?.reduce((acc, c) => {
            const skuId = c.skuId || c.sku
            if (skuId) acc[skuId] = c.name
            return acc
          }, {} as Record<string, string>) || {}}
          defaultSkuNumbers={glasses.colors?.reduce((acc, c) => {
            const skuId = c.skuId || c.sku
            if (skuId) acc[skuId] = c.sku
            return acc
          }, {} as Record<string, string>) || {}}
          translations={add ? pendingTranslations : translations}
          onChange={add ? setPendingTranslations : setTranslations}
          savedLanguages={savedLanguages}
          onSaveSuccess={add ? undefined : async () => {
            if (!productId || !glasses.colors?.length) return
            const skuIds = glasses.colors?.map((c) => c.skuId || c.sku).filter(Boolean) || []
            if (skuIds.length > 0) {
              try {
                const translationsData = await tintVTOService.getProductTranslations(productId, skuIds)
                const loadedProductName = translationsData.alternateName || []
                const loadedSkuNames = (translationsData.alternateSKUsName || []).reduce((acc, item) => {
                  acc[item.skuId] = item.skuI8N || []
                  return acc
                }, {} as Record<string, Array<{ code: string; name: string }>>)

                setTranslations({
                  productName: loadedProductName,
                  skuNames: loadedSkuNames,
                })
                setSavedLanguages(loadedProductName.map((t) => t.code))
              } catch (error) {
                console.error("Failed to load translations:", error)
              }
            }
          }}
        />
      )}
    </Box>
  )
}
